import * as React from "react";

const COMPACT_VIEWPORT_BREAKPOINT = 768;

const getServerSnapshotFalse = () => false;

/**
 * True when viewport width is strictly less than `maxWidthExclusive`.
 * False on the server and hydration render; the first client render reads
 * the live viewport width and re-renders on media-query changes.
 */
export function useIsMaxWidth(maxWidthExclusive: number) {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(`(max-width: ${maxWidthExclusive - 1}px)`);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [maxWidthExclusive],
  );
  const getSnapshot = React.useCallback(
    () => window.innerWidth < maxWidthExclusive,
    [maxWidthExclusive],
  );
  return React.useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshotFalse,
  );
}

/**
 * True when the viewport is too narrow for the desktop layout: a phone, but
 * also a narrow desktop window or browser side panel. A layout signal only;
 * for input capabilities use `useIsTouchPrimary` or `useHasHoverPointer`.
 */
export function useIsCompactViewport() {
  return useIsMaxWidth(COMPACT_VIEWPORT_BREAKPOINT);
}

const HOVER_POINTER_QUERY = "(hover: hover) and (pointer: fine)";

function subscribeToHoverPointer(onChange: () => void) {
  const mql = window.matchMedia(HOVER_POINTER_QUERY);
  mql.addEventListener("change", onChange);
  return () => mql.removeEventListener("change", onChange);
}

/**
 * True when the primary pointer can hover and is precise (mouse/trackpad).
 * False on touch-first devices regardless of viewport width, so hover-driven
 * affordances can stay available in narrow desktop windows.
 *
 * Read synchronously on mount (server snapshot false) so hover-gated chrome
 * renders on the first client paint instead of blinking in a frame later.
 */
export function useHasHoverPointer() {
  return React.useSyncExternalStore(
    subscribeToHoverPointer,
    () => window.matchMedia(HOVER_POINTER_QUERY).matches,
    () => false,
  );
}

export const TOUCH_PRIMARY_QUERY = "(pointer: coarse)";

function subscribeToTouchPrimary(onChange: () => void) {
  const mql = window.matchMedia(TOUCH_PRIMARY_QUERY);
  mql.addEventListener("change", onChange);
  return () => mql.removeEventListener("change", onChange);
}

/**
 * True when the primary pointer is touch, independent of viewport width: a
 * phone or tablet, not a narrow desktop window or browser side panel. Use it
 * for behaviour driven by the on-screen keyboard rather than by layout.
 */
export function useIsTouchPrimary() {
  return React.useSyncExternalStore(
    subscribeToTouchPrimary,
    () => window.matchMedia(TOUCH_PRIMARY_QUERY).matches,
    () => false,
  );
}
